import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { Component, Inject, ElementRef, ViewChild, ViewEncapsulation, OnDestroy } from '@angular/core';
import { FormControl, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteSelectedEvent, MatAutocompleteModule }
	from '@angular/material/autocomplete';
import { MatChipInputEvent, MatChipGrid, MatChipsModule }
	from '@angular/material/chips';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule }
	from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { of, Subscription, debounceTime, distinctUntilChanged, switchMap, tap, startWith } from 'rxjs';
import { MatOptionModule } from '@angular/material/core';
import { NgIf, NgFor } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { TranslateModule } from '@ngx-translate/core';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';

import { pageTransitions } from '../../animations/page-transitions';
import { DatacenterType, UserDatacenterView, UserValueSelected } from '../../models/datacenter-aggrid-view';
import { DatacenterViewService } from '../../services/datacenter-view.service';
import { TranslationService } from '../../services/translation.service';
import { LoadingSpinnerComponent } from '../../components/utils/loading-spinner.component';

export interface ShareViewDialogData {
	name?: string;
	datacenterType: DatacenterType;
}

@Component({
	selector: 'copo-datacenter-share-view',
	templateUrl: './datacenter-share-view-dialog.component.html',
	styleUrl: './datacenter-share-view-dialog.component.css',
	animations: pageTransitions,
	encapsulation: ViewEncapsulation.Emulated,
	imports: [

		MatButtonModule,
		MatIconModule,
		TranslateModule,

		MatFormFieldModule,

		MatInputModule,
		FormsModule,
		ReactiveFormsModule,
		NgIf,

		MatChipsModule,
		NgFor,

		MatAutocompleteModule,

		MatOptionModule,
		LoadingSpinnerComponent,

		MatDialogModule
	]
})
export class DatacenterShareViewDialogComponent implements OnDestroy {
	@ViewChild('userInput') userInput!: ElementRef<HTMLInputElement>;

	@ViewChild('chipList') chipList!: MatChipGrid;

	separatorKeysCodes: number[] = [ENTER, COMMA];

	suggestiveControl = new FormControl();
	viewName = new FormControl();
	placeholder?: string;
	shareViewName: string | undefined;
	selectedUsers: string[] = [];
	filterValue?: UserValueSelected;
	filteredUsers?: UserValueSelected[];
	selectedUserIDs: number[] = [];
	selectedUserObjects: UserValueSelected[] = [];
	isLoading = false;
	viewShared?: Subscription;

	private readonly textSub: Subscription;

	constructor(
		private readonly viewService: DatacenterViewService,

		private readonly dialogRef: MatDialogRef<DatacenterShareViewDialogComponent>,
		private readonly translationService: TranslationService,

		private readonly snackBar: MatSnackBar,

		@Inject(MAT_DIALOG_DATA) public dialogData: ShareViewDialogData
	) {
		this.shareViewName = this.viewService.viewToEdit?.currentView.name;
		this.viewName = new FormControl('', { validators: Validators.required });
		this.suggestiveControl = new FormControl('', { validators: Validators.required });
		this.textSub = this.suggestiveControl.valueChanges.pipe(
			startWith(this.suggestiveControl.value),
			debounceTime(300),
			distinctUntilChanged(),
			tap(() => {
				this.filteredUsers = [];
				this.isLoading = true;
			}),
			switchMap((searchText: string) => this.getRequestObservable(searchText)),
		).subscribe(users => {
			this.filteredUsers = users;
			this.isLoading = false;
		});
	}

	closeDialog() {
		this.dialogRef.close();
	}

	onChangeUser(event: MatChipInputEvent) {
		if (!event.value) {
			return;
		}
		this.selectedUsers.push(event.value);
		event.value = '';
		this.suggestiveControl.reset('');
	}

	remove(user: string) {
		const index = this.selectedUsers.indexOf(user);
		const userObjectindex = this.selectedUserObjects
			.findIndex(value => `${ value.firstName } ${ value.lastName }` === user);
		const selectedId = this.selectedUserObjects[userObjectindex].id;
		const selectedIdIndex = this.selectedUserIDs.indexOf(selectedId);
		if (selectedIdIndex !== -1) {
			this.selectedUserIDs.splice(selectedIdIndex, 1);
		}

		if (index !== -1) {
			this.selectedUsers.splice(index, 1);
		}
		this.chipValidation();
	}

	onSelectUser(event: MatAutocompleteSelectedEvent) {
		if (!event.option.value || !event.option.viewValue) {
			return;
		}
		this.selectedUsers.push(event.option.viewValue);
		this.userInput.nativeElement.value = '';
		this.suggestiveControl.reset('');
		this.selectedUserIDs.push((<UserValueSelected>event.option.value).id);
		this.selectedUserObjects.push(<UserValueSelected>(event.option.value));
		this.chipValidation();
	}

	shareViewWithUsers() {
		const { viewToEdit } = this.viewService;
		if (viewToEdit?.currentView && this.shareViewName && this.selectedUserIDs.length > 0) {
			const newView: UserDatacenterView = {
				id: viewToEdit.currentView.id,
				name: this.shareViewName || viewToEdit.currentView.name,
				isFavorite: Boolean(viewToEdit.currentView.isFavorite),
				type: viewToEdit.viewType,
				columnState: viewToEdit.currentView.columnState,
				sortModel: viewToEdit.currentView.sortModel,
				filterState: viewToEdit.currentView.filterState,
			};

			this.viewShared = this.viewService.shareUserView(newView, this.selectedUserIDs).subscribe({
				next: () => {
					this.dialogRef.close();
					this.openShareViewSnackBar(newView.name);
				},
				error: (error: unknown) => {
					console.error(error);
				}
			});
		}
	}

	openShareViewSnackBar(view: string) {
		const sharedSuccessMessage = this.translationService.getTR('shareview.success.message');
		this.snackBar.open(
			`${ view } ${ sharedSuccessMessage }`,
			undefined,
			{
				duration: 2000,
			}
		);
	}

	validateUsers() {
		return this.selectedUsers.length === 0;
	}

	chipValidation() {
		this.chipList.errorState = Boolean(this.validateUsers());
	}

	getServiceCode(): string {
		const { datacenterType } = this.dialogData;
		let serviceCode;
		switch (datacenterType) {
		case 'document':
			serviceCode = 'PDOCS';
			break;
		case 'quality':
			serviceCode = 'QUAPP';
			break;
		case 'schedule-business-order':
			serviceCode = 'PSBORDT';
			break;
		default:
			serviceCode = 'PORDT';
			break;
		}
		return serviceCode;
	}

	ngOnDestroy() {
		this.textSub.unsubscribe();
	}

	private getRequestObservable(query: string) {
		if (this.getServiceCode() === '') {
			return of([]);
		}
		return this.viewService.getUserBySearchText(query, this.getServiceCode());
	}
}
